import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  ArrowLeft,
  User,
  Phone,
  Mail,
  MapPin,
  Home,
  Calendar,
  Flame,
  Euro,
  Users,
  Heart,
  Briefcase,
  MessageSquare,
  Save,
  Loader2,
  CheckCircle2,
  Clock,
  XCircle,
  PhoneOff,
  CalendarCheck,
  UserX,
  AlertTriangle,
  Plus,
  Trash2,
  FileText,
  UserPlus,
  CalendarPlus,
  Building,
  Send,
  History,
  PhoneCall,
  Calculator
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import SendUploadLinkModal from "@/components/SendUploadLinkModal";

const API = `${typeof window !== "undefined" ? window.location.origin : ""}/api`;

const STATUS_CONFIG = {
  a_appeler: { label: "À appeler", icon: Phone, color: "text-blue-600", bg: "bg-blue-500/10", border: "border-blue-500/30" },
  rappeler: { label: "À rappeler", icon: Clock, color: "text-amber-600", bg: "bg-amber-500/10", border: "border-amber-500/30" },
  ne_repond_pas: { label: "Ne répond pas", icon: PhoneOff, color: "text-gray-600", bg: "bg-muted/30", border: "border-gray-200" },
  rdv_pris: { label: "RDV pris", icon: CalendarCheck, color: "text-purple-600", bg: "bg-purple-500/10", border: "border-purple-200" },
  converti: { label: "Converti", icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
  refuse: { label: "Refusé", icon: XCircle, color: "text-red-600", bg: "bg-red-500/10", border: "border-red-500/30" },
  non_eligible: { label: "Non éligible", icon: UserX, color: "text-orange-600", bg: "bg-orange-50", border: "border-orange-200" },
};

const PRECARITE_CONFIG = {
  BLEU: { label: "Très modeste", color: "bg-blue-500", textColor: "text-blue-700" },
  JAUNE: { label: "Modeste", color: "bg-yellow-500/100", textColor: "text-yellow-700" },
  VIOLET: { label: "Intermédiaire", color: "bg-purple-500/100", textColor: "text-purple-700" },
  ROSE: { label: "Supérieur", color: "bg-pink-500", textColor: "text-pink-700" },
};

const CHAUFFAGE_LABELS = {
  FIOUL: "Fioul",
  GAZ: "Gaz",
  CHEMINEE: "Cheminée",
  POELE_A_BOIS: "Poêle à bois",
  ELECTRIQUE: "Électrique"
};

const PROPRIETE_LABELS = {
  proprietaire_occupant: "Propriétaire occupant",
  bailleur: "Bailleur",
  locataire: "Locataire"
};

const SITUATION_LABELS = {
  marie: "Marié(e)",
  celibataire: "Célibataire",
  pacse: "Pacsé(e)",
  divorce: "Divorcé(e)",
  veuf: "Veuf/Veuve"
};

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [showRdvDialog, setShowRdvDialog] = useState(false);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [converting, setConverting] = useState(false);
  const [showUploadLinkModal, setShowUploadLinkModal] = useState(false);
  const [busySlots, setBusySlots] = useState([]);

  const [formData, setFormData] = useState({});
  const [rdvData, setRdvData] = useState({
    date: "",
    heure: "",
    attribue_a: "",
    notes: ""
  });

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    fetchLead();
    fetchConfig();
  }, [id]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const fetchLead = async () => {
    try {
      const response = await axios.get(`${API}/leads/${id}`);
      setLead(response.data);
      setFormData(response.data);
    } catch (error) {
      console.error("Error fetching lead:", error);
      toast.error("Erreur lors du chargement du lead");
      navigate("/leads");
    } finally {
      setLoading(false);
    }
  };

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/leads/config/options`);
      setConfig(response.data);
    } catch (error) {
      console.error("Error fetching config:", error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/leads/${id}`, formData);
      // If telepro_assigne changed, also update any associated dossier
      if (formData.telepro_assigne && formData.telepro_assigne !== lead?.telepro_assigne) {
        try {
          // Find dossiers linked to this lead and update their assignment
          const dossierRes = await axios.get(`${API}/dossiers`);
          const linkedDossiers = (dossierRes.data || []).filter(d => 
            d.client_name === `${lead.nom || ""} ${lead.prenom || ""}`.trim() ||
            d.notes?.includes(lead.nom)
          );
          for (const d of linkedDossiers) {
            await axios.put(`${API}/dossiers/${d.id}`, { assigned_to: formData.telepro_assigne });
          }
          if (linkedDossiers.length > 0) {
            toast.success(`Attribution mise à jour sur ${linkedDossiers.length} dossier(s)`);
          }
        } catch (dossierErr) {
        }
      }
      toast.success("Lead mis à jour");
      fetchLead();
    } catch (error) {
      console.error("Error updating lead:", error);
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      // For "ne_repond_pas", always allow re-clicking to increment counter
      if (newStatus === "ne_repond_pas") {
        const nrpCount = (lead.ne_repond_pas_count || 0) + 1;
        const nrpDates = [...(lead.ne_repond_pas_dates || []), new Date().toISOString()];
        const history = [...(lead.status_history || []), {
          from: lead.statut,
          to: "ne_repond_pas",
          date: new Date().toISOString(),
        }];
        await axios.put(`${API}/leads/${id}`, {
          statut: newStatus,
          ne_repond_pas_count: nrpCount,
          ne_repond_pas_dates: nrpDates,
          status_history: history,
        });
      } else {
        await axios.put(`${API}/leads/${id}`, { statut: newStatus });
      }
      toast.success("Statut mis à jour");
      fetchLead();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleAddContact = () => {
    const contacts = formData.contacts_supplementaires || [];
    setFormData({
      ...formData,
      contacts_supplementaires: [...contacts, { nom: "", telephone: "", relation: "" }]
    });
  };

  const handleRemoveContact = (index) => {
    const contacts = [...(formData.contacts_supplementaires || [])];
    contacts.splice(index, 1);
    setFormData({ ...formData, contacts_supplementaires: contacts });
  };

  // Fetch busy slots when date/person changes
  useEffect(() => {
    if (rdvData.date && rdvData.attribue_a) {
      axios.get(`${API}/planning/busy-slots/${rdvData.attribue_a}?date=${rdvData.date}`)
        .then(r => setBusySlots(r.data.busy_slots || []))
        .catch(() => setBusySlots([]));
    } else {
      setBusySlots([]);
    }
  }, [rdvData.date, rdvData.attribue_a]);

  const handleContactChange = (index, field, value) => {
    const contacts = [...(formData.contacts_supplementaires || [])];
    contacts[index] = { ...contacts[index], [field]: value };
    setFormData({ ...formData, contacts_supplementaires: contacts });
  };

  const handleCreateRdv = async () => {
    if (!rdvData.date || !rdvData.heure || !rdvData.attribue_a) {
      toast.error("Veuillez remplir la date, l'heure et la personne assignée");
      return;
    }
    // Check if slot is busy
    if (busySlots.includes(rdvData.heure.substring(0, 5))) {
      toast.error(`${rdvData.attribue_a} est déjà occupé à cette heure`);
      return;
    }

    setSaving(true);
    try {
      // Step 1: Create a dossier FIRST to get its ID
      let createdDossierId = null;
      try {
        const profileMap = { proprietaire_occupant: "proprietaire_occupant", bailleur: "bailleur", locataire: "proprietaire_occupant" };
        const dossierRes = await axios.post(`${API}/dossiers`, {
          client_name: `${lead.nom || ""} ${lead.prenom || ""}`.trim(),
          client_email: lead.email || "",
          client_phone: lead.telephone || "",
          profile: profileMap[lead.statut_propriete] || "proprietaire_occupant",
          programme: lead.programme_interesse === "174" ? "174" : "MPR",
          zone: lead.zone_climatique || "H1",
          metrage: lead.surface ? parseFloat(lead.surface) : null,
          precarite: lead.precarite_calculee || "",
          code_postal: lead.code_postal || "",
          adresse_logement: `${lead.adresse || ""} ${lead.ville || ""}`.trim(),
          notes: `RDV Admin le ${rdvData.date} à ${rdvData.heure} avec ${rdvData.attribue_a}. ${rdvData.notes || ""}`.trim(),
          statut_dossier: "rdv_admin",
          assigned_to: rdvData.attribue_a,
        });
        createdDossierId = dossierRes.data?.id || null;
      } catch (dossierErr) {
      }

      // Step 2: Create the RDV with the dossier_id
      await axios.post(`${API}/planning/rdvs`, {
        lead_id: id,
        date: rdvData.date,
        heure: rdvData.heure,
        attribue_a: rdvData.attribue_a,
        client_nom: lead.nom,
        client_prenom: lead.prenom,
        client_telephone: lead.telephone,
        client_adresse: `${lead.adresse || ""} ${lead.code_postal || ""} ${lead.ville || ""}`.trim(),
        programme: lead.programme_interesse,
        notes: rdvData.notes,
        created_by: localStorage.getItem("label_energie_user") || "",
        rdv_type: "rdv_admin",
        dossier_id: createdDossierId,
      });

      toast.success(createdDossierId ? "RDV créé et dossier associé !" : "RDV créé !");
      setShowRdvDialog(false);
      setRdvData({ date: "", heure: "", attribue_a: "", notes: "" });
      // Also update lead status to rdv_pris
      await handleStatusChange("rdv_pris");
      fetchLead();
    } catch (error) {
      console.error("Error creating RDV:", error);
      toast.error("Erreur lors de la création du RDV");
    } finally {
      setSaving(false);
    }
  };

  const handleConvertToDossier = async () => {
    if (!formData.programme_interesse) {
      toast.error("Veuillez sélectionner un programme avant la conversion");
      return;
    }

    setConverting(true);
    try {
      const response = await axios.post(`${API}/leads/${id}/convert-to-dossier`);
      toast.success("Lead converti en dossier avec succès !");
      setShowConvertDialog(false);
      // Rediriger vers le nouveau dossier selon le rôle
      const role = localStorage.getItem("label_energie_role") || "";
      const isRegieTelepro = role === "regie" || role === "telepro";
      navigate(isRegieTelepro ? `/dossier-regie/${response.data.dossier_id}` : `/dossiers/${response.data.dossier_id}`);
    } catch (error) {
      console.error("Error converting lead:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la conversion");
    } finally {
      setConverting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 md:p-12 animate-pulse">
        <div className="h-8 bg-muted rounded w-1/3 mb-8"></div>
        <div className="h-96 bg-muted rounded-xl"></div>
      </div>
    );
  }

  if (!lead) return null;

  const statusConfig = STATUS_CONFIG[lead.statut] || STATUS_CONFIG.a_appeler;
  const StatusIcon = statusConfig.icon;
  const precariteConfig = PRECARITE_CONFIG[lead.precarite_calculee];

  return (
    <div className="p-6 md:p-12 animate-fade-in">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate("/leads")}
          className="mb-4 -ml-2 text-muted-foreground hover:text-foreground rounded-xl"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour aux leads
        </Button>

        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="font-heading text-4xl font-extrabold tracking-tight">
                {lead.nom} {lead.prenom}
              </h1>
              <span className={cn(
                "inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium rounded-lg border",
                statusConfig.bg, statusConfig.border, statusConfig.color
              )}>
                <StatusIcon className="w-4 h-4" />
                {statusConfig.label}
              </span>
              <Select value={lead.statut || "a_appeler"} onValueChange={(val) => handleStatusChange(val)}>
                <SelectTrigger className="h-8 w-auto min-w-[140px] text-xs rounded-lg" data-testid="lead-status-select">
                  <SelectValue placeholder="Changer statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="a_appeler">À appeler</SelectItem>
                  <SelectItem value="rappeler">À rappeler</SelectItem>
                  <SelectItem value="ne_repond_pas">Ne répond pas</SelectItem>
                  <SelectItem value="rdv_pris">RDV pris</SelectItem>
                  <SelectItem value="converti">Converti</SelectItem>
                  <SelectItem value="refuse">Refusé</SelectItem>
                  <SelectItem value="non_eligible">Non éligible</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-muted-foreground">
              Lead reçu le {lead.date_reception || format(new Date(lead.created_at), "dd/MM/yyyy", { locale: fr })}
              {lead.telepro_assigne && ` • Assigné à ${lead.telepro_assigne}`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Bouton envoyer lien de dépôt */}
            <Button
              variant="outline"
              onClick={() => setShowUploadLinkModal(true)}
              className="rounded-xl border-emerald-500 text-emerald-600 hover:bg-emerald-500/10"
              data-testid="send-upload-link-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              Envoyer lien de dépôt
            </Button>
            
            {lead.statut !== "converti" && (
              <>
                <Button
                  variant="outline"
                  onClick={() => setShowRdvDialog(true)}
                  className="rounded-xl"
                  data-testid="create-rdv-btn"
                >
                  <CalendarPlus className="w-4 h-4 mr-2" />
                  Planifier RDV
                </Button>
                <Button
                  onClick={() => setShowConvertDialog(true)}
                  className="rounded-xl bg-[hsl(var(--renova-green))] hover:bg-[hsl(var(--renova-green))]/90"
                  data-testid="convert-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Convertir en dossier
                </Button>
              </>
            )}
            {lead.dossier_id && (
              <Button
                onClick={() => navigate(`/dossiers/${lead.dossier_id}`)}
                className="rounded-xl"
                data-testid="view-dossier-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                Voir le dossier
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Précarité calculée */}
      {precariteConfig && (
        <div className={cn(
          "mb-6 p-4 rounded-xl border flex items-center justify-between",
          "bg-gradient-to-r from-white to-gray-50"
        )}>
          <div className="flex items-center gap-3">
            <div className={cn("w-4 h-4 rounded-full", precariteConfig.color)}></div>
            <div>
              <p className="font-semibold">Précarité calculée : {precariteConfig.label}</p>
              <p className="text-sm text-muted-foreground">
                Zone : {lead.zone_geographique === "IDF" ? "Île-de-France" : "Hors Île-de-France"}
              </p>
            </div>
          </div>
          <span className={cn("text-2xl font-bold", precariteConfig.textColor)}>
            {lead.precarite_calculee}
          </span>
        </div>
      )}

      {/* RDV Info */}
      {lead.rdv && (
        <div className="mb-6 p-4 rounded-xl border border-purple-200 bg-purple-500/10">
          <div className="flex items-center gap-3">
            <CalendarCheck className="w-5 h-5 text-purple-600" />
            <div>
              <p className="font-semibold text-purple-900">
                RDV planifié le {lead.rdv.date} à {lead.rdv.heure}
              </p>
              <p className="text-sm text-purple-700">
                Assigné à {lead.rdv.attribue_a}
                {lead.rdv.notes && ` • ${lead.rdv.notes}`}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="infos" className="space-y-6">
        <TabsList className="rounded-xl p-1 bg-muted">
          <TabsTrigger value="infos" className="rounded-lg data-[state=active]:bg-card">
            <User className="w-4 h-4 mr-2" />
            Informations
          </TabsTrigger>
          <TabsTrigger value="logement" className="rounded-lg data-[state=active]:bg-card">
            <Home className="w-4 h-4 mr-2" />
            Logement
          </TabsTrigger>
          <TabsTrigger value="contacts" className="rounded-lg data-[state=active]:bg-card">
            <Users className="w-4 h-4 mr-2" />
            Contacts
          </TabsTrigger>
          <TabsTrigger value="notes" className="rounded-lg data-[state=active]:bg-card">
            <MessageSquare className="w-4 h-4 mr-2" />
            Notes
          </TabsTrigger>
          <TabsTrigger value="historique" className="rounded-lg data-[state=active]:bg-card" data-testid="tab-historique">
            <History className="w-4 h-4 mr-2" />
            Historique
          </TabsTrigger>
        </TabsList>

        {/* Informations Tab */}
        <TabsContent value="infos">
          <div className="border border-border rounded-xl bg-card p-6">
            <h2 className="font-heading font-semibold text-lg mb-6 flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              Informations personnelles
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label>Nom *</Label>
                <Input
                  value={formData.nom || ""}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  className="rounded-lg"
                />
              </div>
              <div className="space-y-2">
                <Label>Prénom</Label>
                <Input
                  value={formData.prenom || ""}
                  onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                  className="rounded-lg"
                />
              </div>
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <Input
                  value={formData.telephone || ""}
                  onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                  className="rounded-lg"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email || ""}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="rounded-lg"
                />
              </div>
              <div className="space-y-2">
                <Label>Statut</Label>
                <Select
                  value={formData.statut || "a_appeler"}
                  onValueChange={(value) => setFormData({ ...formData, statut: value })}
                >
                  <SelectTrigger className="rounded-lg">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
                      const Icon = cfg.icon;
                      return (
                        <SelectItem key={key} value={key}>
                          <div className="flex items-center gap-2">
                            <Icon className={cn("w-4 h-4", cfg.color)} />
                            {cfg.label}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Télépro assigné</Label>
                {(() => {
                  const role = localStorage.getItem("label_energie_role") || "";
                  const isRegieTelepro = role === "regie" || role === "telepro";
                  return isRegieTelepro ? (
                    <div className="px-3 py-2 rounded-lg border border-border bg-muted text-sm font-medium">{formData.telepro_assigne || "—"}</div>
                  ) : (
                <Select
                  value={formData.telepro_assigne || ""}
                  onValueChange={(value) => setFormData({ ...formData, telepro_assigne: value })}
                >
                  <SelectTrigger className="rounded-lg">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {config?.planning_persons?.map((person) => (
                      <SelectItem key={person} value={person}>{person}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                  );
                })()}
              </div>
              <div className="space-y-2">
                <Label>Situation propriété</Label>
                <Select
                  value={formData.statut_propriete || ""}
                  onValueChange={(value) => setFormData({ ...formData, statut_propriete: value })}
                >
                  <SelectTrigger className="rounded-lg">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PROPRIETE_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Situation familiale</Label>
                <Select
                  value={formData.situation_familiale || ""}
                  onValueChange={(value) => setFormData({ ...formData, situation_familiale: value })}
                >
                  <SelectTrigger className="rounded-lg">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(SITUATION_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Programme intéressé</Label>
                <Select
                  value={formData.programme_interesse || ""}
                  onValueChange={(value) => setFormData({ ...formData, programme_interesse: value })}
                >
                  <SelectTrigger className="rounded-lg">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MPR">Programme MPR</SelectItem>
                    <SelectItem value="174">Programme 174</SelectItem>
                    <SelectItem value="cumule">Op. Cumulée</SelectItem>
                    <SelectItem value="sci">Dossier SCI</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Calcul de Précarité */}
            <h3 className="font-semibold mt-8 mb-4 flex items-center gap-2">
              <Calculator className="w-4 h-4 text-muted-foreground" />
              Calcul de précarité
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="border border-border rounded-xl p-4 space-y-2 bg-muted/30">
                <Label className="text-xs font-semibold text-muted-foreground uppercase">Revenu fiscal annuel</Label>
                <Input
                  type="text"
                  value={formData.revenu_fiscal || ""}
                  onChange={(e) => setFormData({ ...formData, revenu_fiscal: e.target.value })}
                  placeholder="Ex: 25000"
                  className="rounded-lg text-lg font-bold"
                  data-testid="input-revenu-fiscal"
                />
              </div>
              <div className="border border-border rounded-xl p-4 space-y-2 bg-muted/30">
                <Label className="text-xs font-semibold text-muted-foreground uppercase">Zone</Label>
                <div className="flex items-center gap-2 h-10 px-3 border rounded-lg bg-background">
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                  <span className="font-semibold">{formData.zone_geographique === "IDF" ? "Île-de-France" : "Hors IDF"}</span>
                </div>
                <p className="text-[10px] text-muted-foreground">Calculée depuis le code postal</p>
              </div>
              <div className="border border-border rounded-xl p-4 space-y-2 bg-muted/30">
                <Label className="text-xs font-semibold text-muted-foreground uppercase">Nombre de personnes</Label>
                <Input
                  type="number" min="1" max="20"
                  value={formData.personnes_foyer || ""}
                  onChange={(e) => setFormData({ ...formData, personnes_foyer: e.target.value })}
                  placeholder="Ex: 3"
                  className="rounded-lg text-lg font-bold"
                  data-testid="input-personnes-foyer"
                />
              </div>
              <div className={cn("border-2 rounded-xl p-4 space-y-2 flex flex-col items-center justify-center",
                formData.precarite_calculee ? `${PRECARITE_CONFIG[formData.precarite_calculee]?.color}/20 border-current` : "border-dashed border-border"
              )}>
                <Label className="text-xs font-semibold text-muted-foreground uppercase">Précarité</Label>
                {formData.precarite_calculee ? (
                  <>
                    <div className={cn("w-6 h-6 rounded-full", PRECARITE_CONFIG[formData.precarite_calculee]?.color)} />
                    <span className={cn("text-lg font-extrabold", PRECARITE_CONFIG[formData.precarite_calculee]?.textColor)}>
                      {formData.precarite_calculee}
                    </span>
                    <span className="text-[10px] text-muted-foreground">{PRECARITE_CONFIG[formData.precarite_calculee]?.label}</span>
                  </>
                ) : (
                  <span className="text-sm text-muted-foreground italic">Non calculée</span>
                )}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Logement Tab */}
        <TabsContent value="logement">
          <div className="border border-border rounded-xl bg-card p-6">
            <h2 className="font-heading font-semibold text-lg mb-6 flex items-center gap-2">
              <Home className="w-5 h-5 text-primary" />
              Informations sur le logement
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Adresse</Label>
                <Input
                  value={formData.adresse || ""}
                  onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                  placeholder="Numéro et nom de rue"
                  className="rounded-lg"
                />
              </div>
              <div className="space-y-2">
                <Label>Ville</Label>
                <Input
                  value={formData.ville || ""}
                  onChange={(e) => setFormData({ ...formData, ville: e.target.value })}
                  className="rounded-lg"
                />
              </div>
              <div className="space-y-2">
                <Label>Code postal</Label>
                <Input
                  value={formData.code_postal || ""}
                  onChange={(e) => setFormData({ ...formData, code_postal: e.target.value })}
                  className="rounded-lg"
                />
              </div>
              <div className="space-y-2">
                <Label>Zone géographique</Label>
                <div className="flex items-center gap-2 h-10 px-3 border rounded-lg bg-muted/50">
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                  <span>{formData.zone_geographique === "IDF" ? "Île-de-France" : "Hors Île-de-France"}</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Type de logement</Label>
                <Select
                  value={formData.logement || ""}
                  onValueChange={(value) => setFormData({ ...formData, logement: value })}
                >
                  <SelectTrigger className="rounded-lg">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Maison">Maison</SelectItem>
                    <SelectItem value="Appartement">Appartement</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Ancienneté du logement</Label>
                <Input
                  value={formData.anciennete_logement || ""}
                  onChange={(e) => setFormData({ ...formData, anciennete_logement: e.target.value })}
                  placeholder="Ex: Plus de 15 ans"
                  className="rounded-lg"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Système de chauffage actuel</Label>
                <Select
                  value={formData.systeme_chauffage || ""}
                  onValueChange={(value) => setFormData({ ...formData, systeme_chauffage: value })}
                >
                  <SelectTrigger className="rounded-lg">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CHAUFFAGE_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <Flame className="w-4 h-4 text-orange-500" />
                          {label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Contacts Tab */}
        <TabsContent value="contacts">
          <div className="border border-border rounded-xl bg-card p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                Contacts supplémentaires
              </h2>
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddContact}
                className="rounded-lg"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter un contact
              </Button>
            </div>

            {(!formData.contacts_supplementaires || formData.contacts_supplementaires.length === 0) ? (
              <div className="text-center py-12 text-muted-foreground">
                <UserPlus className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>Aucun contact supplémentaire</p>
                <p className="text-sm mt-1">Ajoutez des contacts (conjoint, enfant, etc.)</p>
              </div>
            ) : (
              <div className="space-y-4">
                {formData.contacts_supplementaires.map((contact, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 border rounded-lg bg-muted/30">
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Nom</Label>
                        <Input
                          value={contact.nom || ""}
                          onChange={(e) => handleContactChange(index, "nom", e.target.value)}
                          placeholder="Nom du contact"
                          className="rounded-lg"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Téléphone</Label>
                        <Input
                          value={contact.telephone || ""}
                          onChange={(e) => handleContactChange(index, "telephone", e.target.value)}
                          placeholder="06 XX XX XX XX"
                          className="rounded-lg"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Relation</Label>
                        <Input
                          value={contact.relation || ""}
                          onChange={(e) => handleContactChange(index, "relation", e.target.value)}
                          placeholder="Ex: Conjoint, Enfant"
                          className="rounded-lg"
                        />
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveContact(index)}
                      className="rounded-lg text-destructive hover:text-destructive mt-6"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes">
          <div className="border border-border rounded-xl bg-card p-6">
            <h2 className="font-heading font-semibold text-lg mb-6 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-primary" />
              Notes et commentaires
            </h2>

            <div className="space-y-6">
              <div className="space-y-2">
                <Label>Commentaire général</Label>
                <Textarea
                  value={formData.commentaire || ""}
                  onChange={(e) => setFormData({ ...formData, commentaire: e.target.value })}
                  placeholder="Notes sur le lead..."
                  className="rounded-lg min-h-[120px]"
                />
              </div>
              <div className="space-y-2">
                <Label>Commentaire Mendy</Label>
                <Textarea
                  value={formData.commentaire_mendy || ""}
                  onChange={(e) => setFormData({ ...formData, commentaire_mendy: e.target.value })}
                  placeholder="Notes Mendy..."
                  className="rounded-lg min-h-[120px]"
                />
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Historique Tab */}
        <TabsContent value="historique">
          <div className="border border-border rounded-xl bg-card p-6" data-testid="historique-tab-content">
            <h2 className="font-heading font-semibold text-lg mb-6 flex items-center gap-2">
              <History className="w-5 h-5 text-primary" />
              Historique des changements de statut
            </h2>

            {/* Ne répond pas counter + re-click */}
            <div className="mb-6 p-4 rounded-xl border-2 border-dashed border-gray-300 bg-muted/30/50 flex items-center justify-between" data-testid="nrp-section">
              <div className="flex items-center gap-3">
                <PhoneOff className="w-5 h-5 text-gray-500" />
                <div>
                  <p className="font-semibold text-sm">Ne répond pas</p>
                  <p className="text-xs text-muted-foreground">
                    {lead.ne_repond_pas_count || 0} tentative(s)
                    {(lead.ne_repond_pas_dates || []).length > 0 && (
                      <> — Dernière : {new Date((lead.ne_repond_pas_dates || []).slice(-1)[0]).toLocaleString("fr-FR")}</>
                    )}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleStatusChange("ne_repond_pas")}
                className="rounded-xl border-gray-400 text-gray-700 hover:bg-muted/50"
                data-testid="btn-ne-repond-pas"
              >
                <PhoneCall className="w-4 h-4 mr-2" />
                Tentative d'appel
              </Button>
            </div>

            {/* NRP dates list */}
            {(lead.ne_repond_pas_dates || []).length > 0 && (
              <div className="mb-6 space-y-1">
                <p className="text-xs font-bold text-muted-foreground uppercase mb-2">Détail des tentatives "Ne répond pas"</p>
                {(lead.ne_repond_pas_dates || []).map((d, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                    <div className="w-2 h-2 rounded-full bg-gray-400" />
                    <span>Tentative #{i + 1} — {new Date(d).toLocaleDateString("fr-FR")} à {new Date(d).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Full status history timeline */}
            {(lead.status_history || []).length > 0 ? (
              <div className="space-y-3">
                {[...(lead.status_history || [])].reverse().map((entry, i) => {
                  const fromCfg = STATUS_CONFIG[entry.from] || {};
                  const toCfg = STATUS_CONFIG[entry.to] || {};
                  const ToIcon = toCfg.icon || Clock;
                  const dt = entry.date ? new Date(entry.date) : null;
                  return (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-xl border border-border bg-muted/20" data-testid={`history-entry-${i}`}>
                      <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5", toCfg.bg || "bg-muted/30")}>
                        <ToIcon className={cn("w-4 h-4", toCfg.color || "text-gray-600")} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm">
                          <span className={cn("font-semibold", fromCfg.color)}>{fromCfg.label || entry.from}</span>
                          <span className="mx-2 text-muted-foreground">&rarr;</span>
                          <span className={cn("font-semibold", toCfg.color)}>{toCfg.label || entry.to}</span>
                        </p>
                        {dt && (
                          <p className="text-[10px] text-muted-foreground">
                            {dt.toLocaleDateString("fr-FR")} à {dt.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <History className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p>Aucun changement de statut enregistré</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Save Button */}
      <div className="flex justify-end mt-6">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="rounded-xl bg-[hsl(var(--renova-green))] hover:bg-[hsl(var(--renova-green))]/90"
          data-testid="save-lead-btn"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Enregistrer les modifications
        </Button>
      </div>

      {/* RDV Dialog */}
      <Dialog open={showRdvDialog} onOpenChange={setShowRdvDialog}>
        <DialogContent className="rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Planifier un RDV</DialogTitle>
            <DialogDescription>
              Créer un RDV pour {lead.nom} {lead.prenom}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={rdvData.date}
                  onChange={(e) => setRdvData({ ...rdvData, date: e.target.value })}
                  className="rounded-lg"
                />
              </div>
              <div className="space-y-2">
                <Label>Heure *</Label>
                <Select value={rdvData.heure} onValueChange={(v) => setRdvData({ ...rdvData, heure: v })}>
                  <SelectTrigger className="rounded-lg"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 12 }, (_, i) => {
                      const h = `${String(i + 9).padStart(2, '0')}:00`;
                      const isBusy = busySlots.includes(h);
                      return (
                        <SelectItem key={h} value={h} disabled={isBusy}>
                          {h}{isBusy ? " — Occupé" : ""}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Attribuer à *</Label>
              <Select
                value={rdvData.attribue_a}
                onValueChange={(value) => setRdvData({ ...rdvData, attribue_a: value })}
              >
                <SelectTrigger className="rounded-lg">
                  <SelectValue placeholder="Sélectionner une personne" />
                </SelectTrigger>
                <SelectContent>
                  {config?.planning_persons?.map((person) => (
                    <SelectItem key={person} value={person}>{person}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={rdvData.notes}
                onChange={(e) => setRdvData({ ...rdvData, notes: e.target.value })}
                placeholder="Notes pour le RDV..."
                className="rounded-lg"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRdvDialog(false)} className="rounded-lg">
              Annuler
            </Button>
            <Button
              onClick={handleCreateRdv}
              disabled={saving}
              className="rounded-lg bg-[hsl(var(--renova-green))] hover:bg-[hsl(var(--renova-green))]/90"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CalendarPlus className="w-4 h-4 mr-2" />}
              Créer le RDV
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert Dialog */}
      <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <DialogContent className="rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Convertir en dossier</DialogTitle>
            <DialogDescription>
              Créer un dossier client à partir de ce lead. Les informations seront transférées automatiquement.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {!formData.programme_interesse && (
              <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-800 mb-4">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  <p className="font-medium">Programme non sélectionné</p>
                </div>
                <p className="text-sm mt-1">Veuillez sélectionner un programme avant la conversion.</p>
              </div>
            )}
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Client</span>
                <span className="font-medium">{lead.nom} {lead.prenom}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Programme</span>
                <span className="font-medium">{formData.programme_interesse || "Non défini"}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Précarité</span>
                <span className="font-medium">{PRECARITE_CONFIG[lead.precarite_calculee]?.label || "Non calculée"}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Statut</span>
                <span className="font-medium">{PROPRIETE_LABELS[formData.statut_propriete] || "Non défini"}</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvertDialog(false)} className="rounded-lg">
              Annuler
            </Button>
            <Button
              onClick={handleConvertToDossier}
              disabled={converting || !formData.programme_interesse}
              className="rounded-lg bg-[hsl(var(--renova-green))] hover:bg-[hsl(var(--renova-green))]/90"
            >
              {converting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              Convertir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Modal pour envoyer un lien de dépôt client */}
      <SendUploadLinkModal
        isOpen={showUploadLinkModal}
        onClose={() => setShowUploadLinkModal(false)}
        entityType="lead"
        entityId={id}
        clientName={`${lead?.nom || ''} ${lead?.prenom || ''}`.trim()}
        clientEmail={lead?.email}
      />
    </div>
  );
}
